import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { VerificationStatus } from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import { TAdverseMediaStatus } from './types';
import { ascOrderRecords } from './utils';

// Define the props for our component, which expects an array of the API data
export type TAdverseMediaHistoryProps = {
  history: Partial<TAdverseMediaStatus>[];
};

/**
 * AdverseMediaHistory Component
 * Renders a table displaying the history of adverse media screenings.
 */
export default function AdverseMediaHistory({ history }: TAdverseMediaHistoryProps) {
  // If there's no history, don't render anything.
  if (!history || history.length === 0) {
    return null;
  }

  const sortedHistory = ascOrderRecords(history).reverse();

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: `1px solid ${tokens.colorGrey050}` }}
      data-testid="adverse-media-history"
      data-component="adverse-media-history"
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="caption" color="textSecondary">
                DATE & TIME
              </Typography>
            </TableCell>
            <TableCell>
              <Box alignContent="center" display="flex" justifyContent="center">
                <Typography variant="caption" color="textSecondary">
                  STATUS
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Typography variant="caption" color="textSecondary">
                COMMENT / SOURCE
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedHistory.map((record, index) => (
            <TableRow
              key={record.id}
              data-testid={`adverse-media-history-row-${index}`}
              data-record-id={record.id}
              data-record-type="adverse-media-history"
              data-record-status={record.adverseMediaStatus ? 'matched' : 'not-matched'}
              data-record-update-time={record.lastUpdatedDatetime}
            >
              <TableCell>
                <Typography variant="body2">
                  {formatToDDMMMYYYYHHMMAAA(record.lastUpdatedDatetime)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box alignContent="center" display="flex" justifyContent="center">
                  <VerificationStatus
                    status={record.adverseMediaStatus ? 'Matched' : 'NotMatched'}
                    showLabel={false}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  // The commentText from the API supports HTML markup (e.g., for links).
                  // We use dangerouslySetInnerHTML because the content is sanitized
                  // by the backend before being stored, mitigating XSS risks.
                  dangerouslySetInnerHTML={{ __html: record.commentText }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
